import { useMutation } from "@apollo/client";
import { Alert, Button, CircularProgress, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";

import { VoiceMessage } from "../../../types/api";
import { GET_ALL_ACTIVITIES, UPLOAD_VOICE_MESSAGE } from "../queries";

export const ActivityUpload = ({ onActivityCreated }: ActivityUploadProps): JSX.Element => {
  const [uploadVoiceMessage, { loading, error }] = useMutation<UploadResponse>(
    UPLOAD_VOICE_MESSAGE,
    {
      refetchQueries: [{ query: GET_ALL_ACTIVITIES }, "GetAllActivities"],
    }
  );
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const getCreatedVoiceMessageId = async (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    file: any,
    durationInSeconds: number
  ): Promise<number | undefined> => {
    try {
      const response = await uploadVoiceMessage({
        variables: {
          file,
          durationInSeconds,
        },
      });
      return response.data?.uploadVoiceMessage?.id;
    } catch (e) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to upload voice message");
      setShowError(true);
    }
  };

  const selectFile = async ({
    target: {
      validity,
      files: [file],
    },
  }: // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any) => {
    if (!validity.valid) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error(`file ${file} is invalid`);
      setShowError(true);
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      const audioContext = new window.AudioContext();
      audioContext.decodeAudioData(event.target?.result as ArrayBuffer, async (buffer) => {
        const duration = Math.round(buffer.duration);

        const voiceMessageId = await getCreatedVoiceMessageId(file, duration);
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-extra-boolean-cast
        if (!!voiceMessageId) {
          onActivityCreated(voiceMessageId);
        }
      });
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Button variant="contained" component="label" className="button">
        Upload New Message{" "}
        <input
          type="file"
          accept="audio/x-wav"
          hidden
          onChange={selectFile}
          data-max-size="200000"
        />
      </Button>
      {loading && <CircularProgress />}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert
          onClose={() => {
            setShowError(false);
          }}
          severity="error"
        >
          Failed to upload message.
        </Alert>
      </Snackbar>
    </>
  );
};

interface UploadResponse {
  uploadVoiceMessage: VoiceMessage;
}

export type ActivityUploadProps = {
  onActivityCreated: (voiceMessageId: number) => void;
};
